import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { useLocalStorage } from "base/hooks";

import { AuthContext } from "../context/AuthContext";
import { AppBootScreen } from "./AppBootScreen";
import { isTreezSSOEnabled } from "../data";
import {
    useAuthConfig,
    useExchangeToken,
    useOrganization,
    useUserProfile,
} from "../hooks";
import { TokenData } from "../types";
import { AWSAuthenticatorWrapper } from "./AWSAuthenticatorWrapper";
import { AccessNotAvailable } from "./AccessNotAvailable";

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [tokenData, setTokenData, removeToken] =
        useLocalStorage<TokenData | null>("token", null);
    const [tokenStartDate, setTokenStartDate, removeTokenStartDate] =
        useLocalStorage<number | null>("tokenStartDate", null);
    const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);

    const location = useLocation();

    const callbackUrl =
        typeof window !== "undefined" ? window.location.href : "";

    const { exchangeToken, isExchangingToken } = useExchangeToken({
        onSuccess: tokenData => {
            setTokenData(tokenData);
            // Store the token start date (current timestamp in seconds)
            setTokenStartDate(Math.floor(Date.now() / 1000));
            const url = new URL(window.location.href);
            url.searchParams.delete("code");
            window.history.replaceState({}, "", url.toString());
        },
    });

    const { user, isLoading: isLoadingUser } = useUserProfile(
        tokenData?.accessToken,
    );

    const { organization, isLoading: isLoadingOrganization } = useOrganization({
        accessToken: tokenData?.accessToken,
        orgId: user?.organizationId,
    });

    const ecommConfig = organization?.internalIds?.find(
        (id: { source: string; sourceId?: string }) =>
            id.source === "ecomm-config",
    );
    const parsed = ecommConfig ? JSON.parse(ecommConfig.sourceId ?? {}) : {};
    const stores = parsed.store_ids ?? [];
    const accountId = parsed.account_id ?? "";
    const storeId = parsed.store_ids?.[0] ?? "";

    const { authConfig, isLoading: isLoadingAuthConfig } = useAuthConfig({
        accessToken: tokenData?.accessToken,
        accountId,
    });

    const isLoading =
        isExchangingToken ||
        isLoadingUser ||
        isLoadingOrganization ||
        isLoadingAuthConfig;

    useEffect(() => {
        setSelectedStoreId(storeId);
    }, [storeId]);

    const redirectToLogin = useCallback(() => {
        const url = `${
            process.env.REACT_APP_TREEZ_LOGIN_URL ??
            "https://login.dev.treez.io"
        }?application=MSO&callback=${encodeURIComponent(
            callbackUrl,
        )}&authorize=true&src=mso`;
        window.location.href = url;
    }, [callbackUrl]);

    const redirectToLogout = useCallback(() => {
        const url =
            process.env.REACT_APP_TREEZ_LOGOUT_URL ??
            "https://login.dev.treez.io/signout";

        window.location.href = url;
    }, [callbackUrl]);

    const signoff = useCallback(() => {
        removeToken();
        removeTokenStartDate();
        redirectToLogout();
    }, [removeToken, removeTokenStartDate, redirectToLogout]);

    // Check if token is expired
    const isTokenExpired = useCallback((): boolean => {
        if (!tokenData || !tokenStartDate || !tokenData.expiresIn) {
            return false;
        }
        const currentTimestamp = Math.floor(Date.now() / 1000);
        const expirationTimestamp = tokenStartDate + tokenData.expiresIn;
        return currentTimestamp >= expirationTimestamp;
    }, [tokenData, tokenStartDate]);

    // Function to refresh token by removing it and redirecting to login
    const refreshToken = useCallback(() => {
        removeToken();
        removeTokenStartDate();
        redirectToLogin();
    }, [removeToken, removeTokenStartDate, redirectToLogin]);

    const handleAuthFlow = useCallback(async () => {
        const urlParams = new URLSearchParams(location.search);
        const srcParam = urlParams.get("src");
        const code = urlParams.get("code");

        // If src=mso is present without code, refresh token regardless of expiration
        if (srcParam === "mso" && !code && tokenData?.accessToken) {
            refreshToken();
            return;
        }

        // Check if token exists and is expired
        if (tokenData?.accessToken) {
            if (isTokenExpired()) {
                refreshToken();
                return;
            }
            return;
        }

        if (!code) {
            redirectToLogin();
            return;
        }

        // Exchange code for access token
        exchangeToken(code);
    }, [
        tokenData,
        isTokenExpired,
        location.search,
        redirectToLogin,
        exchangeToken,
        refreshToken,
    ]);

    // Check token expiration on mount and when token data changes
    useEffect(() => {
        if (!isTreezSSOEnabled) return;

        handleAuthFlow();
    }, [
        tokenData,
        tokenStartDate,
        isTokenExpired,
        handleAuthFlow,
        location.search,
        refreshToken,
        isTreezSSOEnabled,
    ]);

    if (isLoading) {
        return <AppBootScreen />;
    }

    if (!storeId && isTreezSSOEnabled) {
        return <AccessNotAvailable />;
    }

    if (!isTreezSSOEnabled) return <AWSAuthenticatorWrapper />;

    return (
        <AuthContext.Provider
            value={{
                user: { ...user, stores },
                loading: isLoading,
                logout: signoff,
                token: tokenData?.accessToken,
                storeId: selectedStoreId,
                accountId,
                updateStoreId: setSelectedStoreId,
                authConfig: authConfig || null,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
